import {
  useCallback, useEffect, useMemo, useReducer, useRef,
} from 'react';
import { message } from 'antd';
import { useActions } from '../../../providers/actions-provider/actions.context';
import { IParsedTable, parseDelimited } from '../../../../utils/parse-csv';
import {
  alignPropertyNames,
  applyImportResults,
  buildImportRequest,
  buildPreviewRows,
  detectMapping,
  IAssetTypeImportResponse,
  IExistingAssetTypes,
  IImportPlan,
  IImportResultSummary,
  IImportSummary,
  IPreviewRow,
  MAX_PREVIEW_ROWS,
  parseJsonAssetTypes,
  ROW_LEVEL_KEY,
  validateImportFile,
  validateImportRows,
} from '../../../../utils/asset-type-import';
import { TWizardStep } from './i-import-wizard';

export interface IWizardState {
  step: TWizardStep;
  fileName: string;
  table: IParsedTable | null;
  plan: IImportPlan | null;
  rows: IPreviewRow[];
  summary: IImportSummary;
  existingCodes: Set<string> | null;
  existingPropertyNames: string[];
  fileError: string | null;
  onlyProblems: boolean;
  busy: boolean;
  response: IAssetTypeImportResponse | null;
  resultSummary: IImportResultSummary | null;
}

type TWizardAction =
  | { type: 'fileParsed'; fileName: string; table: IParsedTable; plan: IImportPlan }
  | { type: 'fileError'; reason: string }
  | { type: 'setPlan'; plan: IImportPlan }
  | { type: 'setExistingTypes'; existing: IExistingAssetTypes }
  | { type: 'setCell'; key: string; field: 'code' | 'description'; value: string }
  | { type: 'setProp'; key: string; index: number; value: string }
  | { type: 'dropRow'; key: string }
  | { type: 'toggleOnlyProblems' }
  | { type: 'goTo'; step: TWizardStep }
  | { type: 'setBusy'; busy: boolean }
  | { type: 'dryRunConflict'; response: IAssetTypeImportResponse }
  | { type: 'submitted'; response: IAssetTypeImportResponse };

const EMPTY_SUMMARY: IImportSummary = {
  total: 0, newCount: 0, duplicateCount: 0, invalidCount: 0,
};

export const initialState: IWizardState = {
  step: 'upload',
  fileName: '',
  table: null,
  plan: null,
  rows: [],
  summary: EMPTY_SUMMARY,
  existingCodes: null,
  existingPropertyNames: [],
  fileError: null,
  onlyProblems: false,
  busy: false,
  response: null,
  resultSummary: null,
};

const summarise = (rows: IPreviewRow[]): IImportSummary => ({
  total: rows.length,
  newCount: rows.filter((row) => row.status === 'new').length,
  duplicateCount: rows.filter((row) => row.status === 'duplicate').length,
  invalidCount: rows.filter((row) => row.status === 'invalid').length,
});

const revalidated = (
  state: IWizardState,
  rows: IPreviewRow[],
  codes: Set<string> | null,
): IWizardState => {
  const validated = validateImportRows(rows, codes);
  return { ...state, rows: validated.rows, summary: validated.summary };
};

export const wizardReducer = (state: IWizardState, action: TWizardAction): IWizardState => {
  switch (action.type) {
    case 'fileParsed': {
      const plan = alignPropertyNames(action.plan, state.existingPropertyNames);
      const rows = buildPreviewRows(action.table, plan);
      return revalidated({
        ...state,
        fileName: action.fileName,
        table: action.table,
        plan,
        fileError: null,
        step: plan.confident ? 'preview' : 'map',
      }, rows, state.existingCodes);
    }
    case 'fileError':
      return {
        ...initialState,
        existingCodes: state.existingCodes,
        existingPropertyNames: state.existingPropertyNames,
        fileError: action.reason,
      };
    case 'setPlan': {
      if (!state.table) {
        return { ...state, plan: action.plan };
      }
      const rows = buildPreviewRows(state.table, action.plan);
      return revalidated({ ...state, plan: action.plan }, rows, state.existingCodes);
    }
    case 'setExistingTypes': {
      const { codes, propertyNames } = action.existing;
      return revalidated({
        ...state,
        existingCodes: codes,
        existingPropertyNames: propertyNames,
        plan: state.plan ? alignPropertyNames(state.plan, propertyNames) : state.plan,
      }, state.rows, codes);
    }
    case 'setCell': {
      const rows = state.rows.map((row) => (
        row.key === action.key ? { ...row, [action.field]: action.value } : row
      ));
      return revalidated(state, rows, state.existingCodes);
    }
    case 'setProp': {
      const rows = state.rows.map((row) => {
        if (row.key !== action.key) {
          return row;
        }
        const props = [...row.props];
        props[action.index] = action.value;
        return { ...row, props };
      });
      return revalidated(state, rows, state.existingCodes);
    }
    case 'dropRow':
      return revalidated(
        state,
        state.rows.filter((row) => row.key !== action.key),
        state.existingCodes,
      );
    case 'toggleOnlyProblems':
      return { ...state, onlyProblems: !state.onlyProblems };
    case 'goTo':
      return { ...state, step: action.step };
    case 'setBusy':
      return { ...state, busy: action.busy };
    case 'dryRunConflict': {
      const verdicts = new Map(action.response.results.map((result) => [result.rowId, result]));
      const rows = state.rows.map((row) => {
        const verdict = verdicts.get(row.key);
        if (!verdict || verdict.status === 'created') {
          return row;
        }
        if (verdict.status === 'duplicate') {
          return {
            ...row,
            status: 'duplicate' as const,
            warnings: {
              ...row.warnings,
              [ROW_LEVEL_KEY]: verdict.message ?? 'Already exists in this tenant',
            },
          };
        }
        return {
          ...row,
          status: 'invalid' as const,
          errors: {
            ...row.errors,
            [ROW_LEVEL_KEY]: verdict.message ?? 'Rejected by the server',
          },
        };
      });
      return {
        ...state, busy: false, rows, summary: summarise(rows),
      };
    }
    case 'submitted': {
      const applied = applyImportResults(state.rows, action.response);
      return {
        ...state,
        busy: false,
        response: action.response,
        rows: applied.rows,
        resultSummary: applied.summary,
        step: 'icons',
      };
    }
    default:
      return state;
  }
};

const readAsText = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (): void => resolve(String(reader.result ?? ''));
  reader.onerror = (): void => reject(reader.error ?? new Error('Could not read the file.'));
  reader.readAsText(file, 'utf-8');
});

const looksLikeJson = (fileName: string, text: string): boolean => {
  if (fileName.toLowerCase().endsWith('.json')) {
    return true;
  }
  const first = text.trimStart().charAt(0);
  return first === '[' || first === '{';
};

export const planIsComplete = (plan: IImportPlan | null): boolean => {
  if (!plan || plan.codeColumn < 0 || plan.descriptionColumn < 0) {
    return false;
  }
  const names = plan.propertyColumns.map((column) => column.name.trim().toLowerCase());
  return names.every((name) => name !== '') && new Set(names).size === names.length;
};

export interface IUseImportAssetTypes {
  state: IWizardState;
  visibleRows: IPreviewRow[];
  samples: string[];
  canContinueFromMap: boolean;
  onFileSelected: (file: File) => void;
  onPlanChange: (plan: IImportPlan) => void;
  onCellChange: (key: string, field: 'code' | 'description', value: string) => void;
  onPropChange: (key: string, index: number, value: string) => void;
  onDropRow: (key: string) => void;
  onToggleOnlyProblems: () => void;
  goTo: (step: TWizardStep) => void;
  submit: () => Promise<void>;
}

export const useImportAssetTypes = (): IUseImportAssetTypes => {
  const [state, dispatch] = useReducer(wizardReducer, initialState);
  const { assetActions } = useActions();
  const mounted = useRef(true);

  useEffect(() => () => {
    mounted.current = false;
  }, []);

  useEffect(() => {
    assetActions.fetchExistingAssetTypes().then((existing) => {
      if (mounted.current) {
        dispatch({ type: 'setExistingTypes', existing });
      }
    });
  }, []);

  const onFileSelected = useCallback((file: File): void => {
    const check = validateImportFile(file);
    if (!check.ok) {
      dispatch({ type: 'fileError', reason: check.reason });
      return;
    }

    readAsText(file)
      .then((text) => {
        if (!mounted.current) {
          return;
        }
        const table = looksLikeJson(file.name, text)
          ? parseJsonAssetTypes(text, { maxRows: MAX_PREVIEW_ROWS })
          : parseDelimited(text, { maxRows: MAX_PREVIEW_ROWS });

        if (table.header.length === 0) {
          dispatch({
            type: 'fileError',
            reason: table.errors[0]?.message ?? 'The file has no readable rows.',
          });
          return;
        }

        dispatch({
          type: 'fileParsed', fileName: file.name, table, plan: detectMapping(table.header),
        });
      })
      .catch(() => {
        if (mounted.current) {
          dispatch({ type: 'fileError', reason: 'Could not read the file.' });
        }
      });
  }, []);

  const submit = useCallback(async (): Promise<void> => {
    const { plan, rows, summary } = state;
    if (!plan || summary.newCount === 0) {
      return;
    }

    dispatch({ type: 'setBusy', busy: true });
    try {
      const dryRun = await assetActions.importAssetTypes(buildImportRequest(rows, plan, true));

      if (dryRun.results.some((result) => result.status !== 'created')) {
        if (mounted.current) {
          dispatch({ type: 'dryRunConflict', response: dryRun });
        }
        message.warning('Some rows changed since you loaded the file. Check the preview.');
        return;
      }

      const response = await assetActions.importAssetTypes(buildImportRequest(rows, plan, false));
      if (mounted.current) {
        dispatch({ type: 'submitted', response });
      }
      message.success(`${response.created ?? summary.newCount} asset types created`);
    } catch {
      if (mounted.current) {
        dispatch({ type: 'setBusy', busy: false });
      }
      message.error('Import failed. Nothing was created.');
    }
  }, [state, assetActions]);

  const visibleRows = useMemo(
    () => (state.onlyProblems ? state.rows.filter((row) => row.status !== 'new') : state.rows),
    [state.rows, state.onlyProblems],
  );

  const samples = useMemo(() => {
    const header = state.table?.header ?? [];
    return header.map((_unused, columnIndex) => {
      const found = (state.table?.rows ?? []).find((cells) => (cells[columnIndex] ?? '') !== '');
      return found ? found[columnIndex] : '';
    });
  }, [state.table]);

  return {
    state,
    visibleRows,
    samples,
    canContinueFromMap: planIsComplete(state.plan),
    onFileSelected,
    onPlanChange: useCallback((plan: IImportPlan) => dispatch({ type: 'setPlan', plan }), []),
    onCellChange: useCallback(
      (key: string, field: 'code' | 'description', value: string) => dispatch({
        type: 'setCell', key, field, value,
      }),
      [],
    ),
    onPropChange: useCallback(
      (key: string, index: number, value: string) => dispatch({
        type: 'setProp', key, index, value,
      }),
      [],
    ),
    onDropRow: useCallback((key: string) => dispatch({ type: 'dropRow', key }), []),
    onToggleOnlyProblems: useCallback(() => dispatch({ type: 'toggleOnlyProblems' }), []),
    goTo: useCallback((step: TWizardStep) => dispatch({ type: 'goTo', step }), []),
    submit,
  };
};
